import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface TypingState {
  [key: string]: {
    isTyping: boolean;
    name: string;
  };
}

export function useTypingIndicator(conversationId: string) {
  const { user, profile } = useAuth();
  const [typingUsers, setTypingUsers] = useState<TypingState>({});
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastTypingRef = useRef<number>(0);

  // Set up presence channel
  useEffect(() => {
    if (!conversationId || !user) return;

    const channel = supabase.channel(`typing:${conversationId}`, {
      config: {
        presence: {
          key: user.id,
        },
      },
    });

    channel
      .on("presence", { event: "sync" }, () => {
        const state = channel.presenceState();
        const newTypingUsers: TypingState = {};
        
        Object.entries(state).forEach(([key, value]) => {
          const presences = value as Array<{ isTyping?: boolean; name?: string }>;
          if (presences.length > 0 && presences[0].isTyping && key !== user.id) {
            newTypingUsers[key] = {
              isTyping: true,
              name: presences[0].name || "Quelqu'un",
            };
          }
        });
        
        setTypingUsers(newTypingUsers);
      })
      .on("presence", { event: "join" }, ({ key, newPresences }) => {
        if (key !== user.id && newPresences?.[0]?.isTyping) {
          setTypingUsers((prev) => ({
            ...prev,
            [key]: {
              isTyping: true,
              name: newPresences[0].name || "Quelqu'un",
            },
          }));
        }
      })
      .on("presence", { event: "leave" }, ({ key }) => {
        if (key !== user.id) {
          setTypingUsers((prev) => {
            const newState = { ...prev };
            delete newState[key];
            return newState;
          });
        }
      })
      .subscribe(async (status) => {
        if (status === "SUBSCRIBED") {
          await channel.track({
            isTyping: false,
            name: profile?.first_name || "Utilisateur",
          });
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [conversationId, user, profile?.first_name]);

  // Send typing indicator
  const sendTyping = useCallback(async () => {
    if (!conversationId || !user) return;

    const now = Date.now();
    // Throttle: only send every 2 seconds
    if (now - lastTypingRef.current < 2000) return;
    lastTypingRef.current = now;

    const channel = supabase.channel(`typing:${conversationId}`);
    
    // Track typing state
    await channel.track({
      isTyping: true,
      name: profile?.first_name || "Utilisateur",
    });

    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Stop typing after 3 seconds of inactivity
    typingTimeoutRef.current = setTimeout(async () => {
      await channel.track({
        isTyping: false,
        name: profile?.first_name || "Utilisateur",
      });
    }, 3000);
  }, [conversationId, user, profile?.first_name]);

  // Stop typing indicator
  const stopTyping = useCallback(async () => {
    if (!conversationId || !user) return;

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    const channel = supabase.channel(`typing:${conversationId}`);
    await channel.track({
      isTyping: false,
      name: profile?.first_name || "Utilisateur",
    });
  }, [conversationId, user, profile?.first_name]);

  // Get typing users as array (excluding current user)
  const typingList = Object.entries(typingUsers)
    .filter(([id, data]) => data.isTyping && id !== user?.id)
    .map(([, data]) => data.name);

  return {
    typingUsers: typingList,
    sendTyping,
    stopTyping,
    isTyping: typingList.length > 0,
  };
}
